import { useEffect, useRef } from 'react'

interface OverlayScrollbarProps {
  targetRef: React.RefObject<HTMLElement | null>
}

/**
 * Fading overlay scrollbar for a native scroll container whose own
 * scrollbar is hidden (via `.overlay-scroll-host`). Keeps the bar from
 * reserving horizontal layout width while still showing a thumb on scroll.
 *
 * The thumb is sized + positioned imperatively (no per-pixel React
 * re-render) on scroll and on resize (ResizeObserver). It reveals while
 * scrolling or while the pointer is over the track, and auto-hides after
 * a short idle delay — matching the macOS "when scrolling" overlay bar.
 *
 * Interactive like a native scrollbar: the thumb is draggable (pointer
 * capture, so the drag keeps tracking outside the strip), and clicking
 * the track jumps the scroll position so the thumb centers on the click.
 *
 * Must be rendered as a sibling of the scroll container inside a
 * `position: relative` box, so it tracks the viewport rather than
 * scrolling away with the content.
 */
const IDLE_HIDE_MS = 800

export function OverlayScrollbar({ targetRef }: OverlayScrollbarProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const scrollEl = targetRef.current
    const trackEl = trackRef.current
    const thumbEl = thumbRef.current
    if (scrollEl === null || trackEl === null || thumbEl === null) return

    let hideTimer: ReturnType<typeof setTimeout> | undefined
    let dragging = false
    let hovering = false
    // Pointer offset within the thumb at drag start, so the grabbed spot
    // stays under the cursor instead of snapping the thumb's top to it.
    let grabOffset = 0

    const layout = (): void => {
      const visible = scrollEl.clientHeight
      const total = scrollEl.scrollHeight
      if (total <= visible) {
        thumbEl.style.display = 'none'
        return
      }
      thumbEl.style.display = ''
      thumbEl.style.height = `${(visible / total) * 100}%`
      thumbEl.style.top = `${(scrollEl.scrollTop / total) * 100}%`
    }

    const show = (): void => {
      thumbEl.style.opacity = '1'
      clearTimeout(hideTimer)
    }

    const scheduleHide = (): void => {
      clearTimeout(hideTimer)
      if (dragging || hovering) return
      hideTimer = setTimeout(() => {
        thumbEl.style.opacity = '0'
      }, IDLE_HIDE_MS)
    }

    const onScroll = (): void => {
      layout()
      show()
      scheduleHide()
    }

    /** Map a track-local Y for the thumb's top edge to a scrollTop. */
    const scrollToThumbTop = (thumbTop: number): void => {
      const trackHeight = trackEl.clientHeight
      if (trackHeight <= 0) return
      scrollEl.scrollTop = (thumbTop / trackHeight) * scrollEl.scrollHeight
    }

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return
      const total = scrollEl.scrollHeight
      if (total <= scrollEl.clientHeight) return
      event.preventDefault()
      const thumbRect = thumbEl.getBoundingClientRect()
      const trackRect = trackEl.getBoundingClientRect()
      if (event.clientY >= thumbRect.top && event.clientY <= thumbRect.bottom) {
        grabOffset = event.clientY - thumbRect.top
      } else {
        // Track click: jump so the thumb centers on the click, then keep
        // dragging from its center (native scrollbar behavior).
        grabOffset = thumbRect.height / 2
        scrollToThumbTop(event.clientY - trackRect.top - grabOffset)
      }
      dragging = true
      show()
      // Pointer capture keeps the drag tracking when the cursor leaves
      // the track. Not implemented in JSDOM, hence the guard.
      if (typeof trackEl.setPointerCapture === 'function') {
        trackEl.setPointerCapture(event.pointerId)
      }
    }

    const onPointerMove = (event: PointerEvent): void => {
      if (!dragging) return
      const trackRect = trackEl.getBoundingClientRect()
      scrollToThumbTop(event.clientY - trackRect.top - grabOffset)
    }

    const endDrag = (event: PointerEvent): void => {
      if (!dragging) return
      dragging = false
      if (
        typeof trackEl.hasPointerCapture === 'function' &&
        trackEl.hasPointerCapture(event.pointerId)
      ) {
        trackEl.releasePointerCapture(event.pointerId)
      }
      scheduleHide()
    }

    const onPointerEnter = (): void => {
      hovering = true
      if (scrollEl.scrollHeight > scrollEl.clientHeight) show()
    }

    const onPointerLeave = (): void => {
      hovering = false
      scheduleHide()
    }

    layout()
    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    trackEl.addEventListener('pointerdown', onPointerDown)
    trackEl.addEventListener('pointermove', onPointerMove)
    trackEl.addEventListener('pointerup', endDrag)
    trackEl.addEventListener('pointercancel', endDrag)
    trackEl.addEventListener('pointerenter', onPointerEnter)
    trackEl.addEventListener('pointerleave', onPointerLeave)
    // ResizeObserver isn't available in JSDOM, so tests skip the
    // resize-driven update; production browsers all have it.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(layout) : null
    ro?.observe(scrollEl)
    return () => {
      scrollEl.removeEventListener('scroll', onScroll)
      trackEl.removeEventListener('pointerdown', onPointerDown)
      trackEl.removeEventListener('pointermove', onPointerMove)
      trackEl.removeEventListener('pointerup', endDrag)
      trackEl.removeEventListener('pointercancel', endDrag)
      trackEl.removeEventListener('pointerenter', onPointerEnter)
      trackEl.removeEventListener('pointerleave', onPointerLeave)
      ro?.disconnect()
      clearTimeout(hideTimer)
    }
  }, [targetRef])

  return (
    <div
      ref={trackRef}
      aria-hidden
      className="absolute inset-y-0 right-0 z-10 w-3 touch-none select-none"
    >
      <div
        ref={thumbRef}
        className="absolute right-0.5 w-1.5 rounded-full bg-[var(--line-2)] opacity-0 transition-opacity duration-200"
      />
    </div>
  )
}
